import { ContactMailOutlined, NotificationsOutlined, PersonOutlineRounded, WorkOutlineOutlined } from '@mui/icons-material';
import { Box, Divider, Stack, styled } from '@mui/material';
import { useProfileCompletion } from 'features/profile/hooks';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EntityViewSectionTitle } from 'shared/components';
import { useFeatureEnabled } from 'shared/hooks';

import { CandidateProfileFormData, CandidateViewModel } from '../../types';
import { ProfileCompletionBanner } from '../ProfileCompletionBanner';
import FieldCommunicationPreferences from './fields/FieldCommunicationPreferences';
import FieldEmail from './fields/FieldEmail';
import FieldFirstName from './fields/FieldFirstName';
import FieldLastName from './fields/FieldLastName';
import FieldLocation from './fields/FieldLocation';
import FieldMobilePhone from './fields/FieldMobilePhone';
import FieldRolesSpecialties from './fields/FieldRolesSpecialties';
import FieldSkills from './fields/FieldSkills';

export default function ProfileFormBody({ profile }: { profile: CandidateViewModel }) {
    const { t } = useTranslation('profile');
    const { control } = useFormContext<CandidateProfileFormData>();
    const isRoleSpecialtyEnabled = useFeatureEnabled('RoleSpecialtyTaxonomy');

    const [
        firstName,
        lastName,
        location,
        roles,
        roleSpecialties,
        skills
    ] = useWatch({ control, name: [ 'firstName', 'lastName', 'location', 'roles', 'roleSpecialties', 'skills' ] });

    const completionData = useMemo(() => ({
        firstName,
        lastName,
        mobilePhone: profile.mobilePhone,
        location,
        roles: isRoleSpecialtyEnabled ? roleSpecialties : roles,
        skills
    }), [ firstName, lastName, profile.mobilePhone, location, isRoleSpecialtyEnabled, roles, roleSpecialties, skills ]);

    const { percentage, tip } = useProfileCompletion(completionData);

    return (
        <Stack gap={ 4 }>
            <ProfileCompletionBanner
                percentage={ percentage }
                tip={ tip }
            />

            <FormControlsContainer >
                <StyledSectionTitle
                    title={ t('view.sections.personalInformation') }
                    icon={ <PersonOutlineRounded /> }
                />
                <FieldFirstName />
                <FieldLastName />

                <StyledDivider />

                <StyledSectionTitle
                    title={ t('view.sections.contactDetails') }
                    icon={ <ContactMailOutlined /> }
                />
                
                <FieldEmail email={ profile.email } />
                <FieldMobilePhone mobilePhone={ profile.mobilePhone } />
                <FieldLocation />

                <StyledDivider />

                <StyledSectionTitle
                    title={ t('view.sections.professionalDetails') }
                    icon={ <WorkOutlineOutlined /> }
                />
                <FieldSkills />
                <FieldRolesSpecialties />

                <StyledDivider />

                <StyledSectionTitle
                    title={ t('view.sections.communicationPreferences') }
                    icon={ <NotificationsOutlined /> }
                />
                    
                <FieldCommunicationPreferences globallyUnsubscribed={ !profile.communicationPreferences.unspecified } />

            </FormControlsContainer>
        </Stack>
    );
}

const FormControlsContainer = styled(Box)(({ theme }) => ({
    display: 'grid',
    gap: theme.spacing(4, 6),

    gridTemplateColumns: '1fr',

    [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: 'repeat(2, 1fr)'
    }
}));

const StyledSectionTitle = styled(EntityViewSectionTitle)({
    gridColumn: '1 / -1'
});

const StyledDivider = styled(Divider)(({ theme }) => ({
    gridColumn: '1 / -1',
    margin: theme.spacing(4, 0, 2)
}));
